import { defineStore } from 'pinia'
import { formatPhoneNumber } from 'src/utils'
import { Platform } from 'quasar'
import { reloadPage } from 'src/utils'
import { identityInstance, removeAVSessionID } from 'boot/axios'
import { WebStorageStateStore } from 'oidc-client'
import CookieStorage from 'src/utils/CookieStorage'
import { useThemeStore } from './theme'
import { usePersistedStore } from 'src/stores/persisted'
import { useCommonStore } from 'src/stores/common'
import { usePersistedLocalStore } from 'src/stores/persisted-local'
import { useProductStore } from 'src/stores/product'

const getUserIdentityKey = (user) => {
  if (!user) return ''
  return (
    user?.profile?.sub ||
    user?.profile?.user_id ||
    user?.id_token ||
    user?.access_token ||
    ''
  )
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    loading: false,
    loadingUser: false,
    currentUser: null,
    exists: false,
  }),
  getters: {
    isLoggedIn: (state) => state.user && state.user.access_token,
    ftxCoreUser: (state) => state.user || null,
    currentUserIdNumber() {
      return this.currentUser?.userIdNumber || ''
    },
    currentUserName() {
      if (this.currentUser)
        return `${this.currentUser.firstName} ${this.currentUser.lastName}`
      return ''
    },
    currentUserImage() {
      if (this.currentUser) return this.currentUser.profileImageUrl
      return ''
    },
    currentUserIdentity() {
      if (this.currentUser)
        if (this.currentUser.email) return this.currentUser.email
        else if (this.currentUser.phoneNumber) {
          let phone = this.currentUser?.countryCode
            ? `${this.currentUser.countryCode}${this.currentUser.phoneNumber}`
            : this.currentUser.phoneNumber
          return formatPhoneNumber(phone)
        } else if (this.currentUser.userIdNumber)
          return this.currentUser.userIdNumber

      return ''
    },
    accessToken: (state) => state.user && state.user.access_token,
    customerExists() {
      return !!(this.isLoggedIn && !!this.exists)
    },
  },
  actions: {
    clearUserScopedListingState() {
      const persistedLocalStore = usePersistedLocalStore()
      const productStore = useProductStore()

      persistedLocalStore.setField({
        field: 'filters',
        value: {},
      })
      productStore.resetPagination()
    },
    async mapUser() {
      try {
        let user = await this.$oidc.getUser()
        if (user) this.user = user
        else this.user = null
        return user
      } catch (error) {
        this.user = null
      }

      return null
    },
    async signinSilent() {
      try {
        return await this.$oidc.signinSilent()
      } catch (error) {}
    },
    async oidcGetUser() {
      try {
        let user = await this.$oidc.getUser()
        return user
      } catch (error) {
        return null
      }
    },
    async getUserInfo() {
      let userInfo = {}
      try {
        this.loadingUser = true
        let { data } = await identityInstance.get('Account')
        if (data.succeeded && data.data) {
          this.currentUser = data.data
          userInfo = data.data
        }
        return userInfo
      } catch (error) {
        return userInfo
      } finally {
        this.loadingUser = false
      }
    },
    async signIn({ isSignup = false, signupAcr = '' } = {}) {
      const persistedStore = usePersistedStore()
      const commonStore = useCommonStore()

      let signInExtraParam = {},
        businessConfigurations = commonStore.businessConfigurations,
        currentRoute = this.$router.currentRoute.value,
        routePath = '',
        forceUpdate = false,
        isBuyNowOrder =
          currentRoute.name == 'Checkout' &&
          currentRoute.query?.session &&
          currentRoute.query?.mode === 'buy-now'

      // If tenant has lns enabled and login from checkout screen then redirect to cart page
      if (isBuyNowOrder) {
        const resolved = await this.$router.resolve(currentRoute.fullPath)
        if (resolved?.fullPath) {
          routePath = resolved.fullPath
          forceUpdate = true
        }
      } else if (
        isBuyNowOrder === false &&
        currentRoute.name == 'Checkout' &&
        [1, 2].includes(businessConfigurations.lnsSubscriptionType)
      )
        routePath = '/cart'
      else if (persistedStore.currentLocationId) routePath = currentRoute.path
      else if (
        !persistedStore.currentLocationId &&
        currentRoute.query.redirectFrom &&
        currentRoute.query.redirectFrom !== '/'
      ) {
        const resolved = await this.$router.resolve(
          currentRoute.query.redirectFrom
        )
        if (resolved?.path) {
          routePath = resolved.path
          forceUpdate = true
        }
      }

      if (routePath && (!persistedStore.authRedirectUri || forceUpdate))
        await persistedStore.setField({
          field: 'authRedirectUri',
          value: routePath,
        })

      let tenantAcr = businessConfigurations.tenantAcr
      let newAcrValues = `tenant:${tenantAcr}`

      // let path = ''
      // if (process.env.SERVER) {
      //   path = process.env.fullSsrUrl
      //   newAcrValues = `tenant:${tenantAcr} signUpUrl:${process.env.fullSsrUrl}/signup`
      // } else if (Platform && Platform.is && Platform.is.cordova) {
      //   path = `${location.protocol}//${location.hostname}${
      //     location.port ? ':' + location.port : ''
      //   }`
      //   newAcrValues = `tenant:${tenantAcr} signUpUrl:${process.env.ANDROID_DEEP_LINK}/signup`
      // } else {
      //   path = `${location.protocol}//${location.hostname}${
      //     location.port ? ':' + location.port : ''
      //   }`

      //   newAcrValues = `tenant:${tenantAcr} signUpUrl:${path}/signup`
      // }

      this.$oidc._settings._acr_values = newAcrValues

      if (isSignup) this.$oidc._settings._acr_values += signupAcr

      // if (socialPlatform)
      //   this.$oidc._settings._acr_values += ` idp:${socialPlatform} aat:signup`

      // if (Platform?.is?.cordova)
      // signInExtraParam = {
      //   extraTokenParams: {
      //     atl: 86400 * 365,
      //   },
      // }

      const themeStore = useThemeStore()
      signInExtraParam = {
        extraQueryParams: {
          logourl: themeStore.theme.logoURL || '',
          faviconurl: themeStore.theme.faviconURL || '',
          backgroundurl: themeStore.theme.backgroundURL || '',
          primarycolor: themeStore.theme.primaryColor || '',
          backgroundcolor: themeStore.theme.backgroundColor || '',
          secondarycolor: themeStore.theme.secondaryColor || '',
          textcolor: themeStore.theme.textColor || '',
          showpoweredby: true,
        },
      }

      // if (Platform.is.mobile || !process.env.LOGIN_ON_POPUP)
      //   this.$oidc.signinRedirect(signInExtraParam)
      // else
      // this.$oidc.signinPopup(signInExtraParam).then((user) => {
      //   reloadPage()
      //   // if (user) commit('SET_USER', user)
      // })

      if (!Platform.is?.cordova) this.$oidc.signinRedirect(signInExtraParam)
      else
        try {
          let user = await this.$oidc.signinPopup(signInExtraParam)
          // if (user) commit('SET_USER', user)
          reloadPage()
          // const commonStore = useCommonStore()
          // commonStore.setState({ key: 'appLoaded', value: false })

          return user
        } catch (error) {
          return null
        }
    },
    logout() {
      const persistedStore = usePersistedStore()
      const persistedLocalStore = usePersistedLocalStore()
      this.clearUserScopedListingState()

      // if (Platform.is.mobile || !process.env.LOGIN_ON_POPUP)
      // Clear EAIV Session on Logout
      const eaivPersistedSessionId = persistedStore.eaivPersistedSessionId
      if (eaivPersistedSessionId) {
        removeAVSessionID()
        persistedStore.setField({
          field: 'eaivPersistedSessionId',
          value: null,
        })
        persistedStore.setField({
          field: 'eaivPersistedVerificationSuccess',
          value: false,
        })
      }

      if (persistedLocalStore.persistedCartNumber) {
        persistedLocalStore.setField({
          field: 'persistedCartNumber',
          value: null,
        })
      }

      if (!Platform.is?.cordova)
        this.$oidc.signoutRedirect().then(() => {
          this.$oidc
            .clearStaleState(
              new WebStorageStateStore({
                store: new CookieStorage(),
              })
            )
            .then(() => {
              console.log('clearState success')
            })
            .catch((e) => {
              console.log('clearStateState error', e.message)
            })
        })
      else
        this.$oidc.signoutPopup().then(() => {
          reloadPage()
        })
    },
    async checkCustomerExits() {
      try {
        let { data } = await this.$axiosInstance.get('/Signup/Exist')
        if (data?.success && data?.data?.alreadyExist) {
          this.exists = true
          return true
        }
        return false
      } catch (error) {
        return false
      }
    },
    async signUpAsCustomer(body) {
      try {
        let { data } = await this.$axiosInstance.post(`/Signup/Exist`, body)
        this.exists = true
        return data
      } catch (error) {
        return error
      }
    },
    async deleteCustomer() {
      try {
        let { data } = await this.$axiosInstance.get('/Customer', {
          params: { global: true },
        })

        return data
      } catch (error) {
        return error
      }
    },
    async setFCMToken(body) {
      try {
        let { data } = await this.$axiosInstance.post(
          '/Notification/Token',
          body
        )

        return data
      } catch (error) {
        return error
      }
    },
    async deleteFCMToken(deviceID) {
      try {
        let { data } = await this.$axiosInstance.delete(
          `/Notification/Token/${deviceID}`
        )

        return data
      } catch (error) {
        return error
      }
    },
    setUser(user) {
      const currentIdentity = getUserIdentityKey(this.user)
      const nextIdentity = getUserIdentityKey(user)

      if (currentIdentity !== nextIdentity) {
        this.clearUserScopedListingState()
      }
      this.user = user
    },
  },
})
